import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material'

import { useAppContext } from '@/hooks'
import { APPROVER_COUNT_MAX } from '@/utils/approverMinimum'

import { useEditBranchDialog } from '../hooks/useEditBranchDialog'
import { useMinimumNumberOfApprovers } from '../hooks/useMinimumNumberOfApprovers'
import { BranchRecord } from '../types'

type EditBranchDialogProps = {
	branch: BranchRecord | null
	branches: BranchRecord[]
	isOpen: boolean
	onClose: () => void
	systemId?: number
}

export const EditBranchDialog = ({
	branch,
	branches,
	isOpen,
	onClose,
	systemId,
}: EditBranchDialogProps) => {
	const { t } = useAppContext()
	const minimumNumberOfApprovers = useMinimumNumberOfApprovers(systemId)
	const form = useEditBranchDialog({
		branch,
		branches,
		isOpen,
		minimumNumberOfApprovers,
		onClose,
	})
	const rangeText = t('BRANCHES_REQUIRED_APPROVALS_RANGE', [
		form.minimumNumberOfApprovers,
		APPROVER_COUNT_MAX,
	])
	const nameHelper =
		form.nameError === 'empty'
			? t('BRANCHES_NAME_REQUIRED')
			: form.nameError === 'taken'
				? t('BRANCHES_NAME_TAKEN')
				: undefined

	return (
		<Dialog
			open={isOpen}
			onClose={form.handleClose}
			maxWidth="sm"
			fullWidth
			slotProps={{ paper: { sx: { borderRadius: '14px' } } }}
		>
			<DialogTitle
				data-testid="modal-header"
				sx={{ fontSize: 18, fontWeight: 700, color: '#1F4954', pb: 1 }}
			>
				{t('BRANCHES_EDIT_DEFINITION')}
			</DialogTitle>
			<DialogContent sx={{ pt: '10px !important' }} data-testid="modal-body">
				<Box sx={{ display: 'grid', gap: 2 }}>
					<TextField
						label={t('NAME')}
						value={form.name}
						onChange={(event) => form.setName(event.target.value)}
						disabled={form.nameLocked}
						error={Boolean(form.nameError)}
						helperText={nameHelper}
						size="small"
						fullWidth
						slotProps={{
							htmlInput: { 'data-testid': 'text-input-branch-name' },
							input: { sx: { borderRadius: '9px', fontSize: 13 } },
						}}
					/>
					<TextField
						label={t('DESCRIPTION')}
						value={form.description}
						onChange={(event) => form.setDescription(event.target.value)}
						size="small"
						fullWidth
						multiline
						minRows={3}
						slotProps={{
							htmlInput: { 'data-testid': 'text-input-branch-description' },
							input: { sx: { borderRadius: '9px', fontSize: 13 } },
						}}
					/>
					<TextField
						label={t('BRANCHES_REQUIRED_APPROVALS')}
						helperText={`${t('BRANCHES_REQUIRED_APPROVALS_HINT')} ${rangeText}`}
						error={!form.approvalsValid}
						value={
							Number.isNaN(form.requiredApprovals) ? '' : form.requiredApprovals
						}
						onChange={(event) => {
							const raw = event.target.value
							if (raw === '') {
								form.setRequiredApprovals(Number.NaN)
								return
							}
							const next = Number(raw)
							form.setRequiredApprovals(
								Number.isInteger(next) ? next : Number.NaN,
							)
						}}
						type="number"
						size="small"
						fullWidth
						slotProps={{
							htmlInput: {
								min: form.minimumNumberOfApprovers,
								max: APPROVER_COUNT_MAX,
								step: 1,
								'data-testid': 'text-input-branch-required-approvals',
							},
							input: { sx: { borderRadius: '9px', fontSize: 13 } },
						}}
					/>
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2.5 }} data-testid="modal-footer">
				<Button
					onClick={form.handleClose}
					data-testid="modal-button-cancel"
					sx={{ textTransform: 'none', borderRadius: '8px' }}
				>
					{t('CANCEL')}
				</Button>
				<Button
					variant="contained"
					disableElevation
					onClick={() => void form.handleSave()}
					disabled={Boolean(form.nameError) || form.isSaving || !form.approvalsValid}
					data-testid="modal-button-save"
					sx={{ textTransform: 'none', borderRadius: '8px', px: 2 }}
				>
					{t('SAVE')}
				</Button>
			</DialogActions>
		</Dialog>
	)
}
